import { Lock, MessageSquare, Phone } from 'lucide-react';

import { Button } from '@/components/atoms/button';
import { formatPhoneDisplay } from '@/lib/contacts/phone';

/**
 * The single-scope estimate's closing sections, shared by the public quote and
 * the portal: a "talk to us before you decide" contact card (rendered only when
 * the business has a phone on file), a "see everything in one place"
 * account prompt, a private-link security notice, and a legal line. Purely
 * presentational — data-gated, never a hardcoded value.
 */
export function SingleScopeFooter({
  phone,
  portalHref,
  companyName,
  companyAddress,
  license,
  expiresLabel,
  issuedNote,
  showSecureNotice = false,
  onDecline,
}: {
  phone?: string | null;
  /** Where "Access your portal" points; omit to hide the account prompt. */
  portalHref?: string | null;
  /** Legal line (`.publegal`) — company identity beneath the closing cards. */
  companyName?: string | null;
  companyAddress?: string | null;
  license?: string | null;
  /** Formatted date the private link stops working (from validUntil). */
  expiresLabel?: string | null;
  /** Document footer sentence (`.dfoot`), e.g. "Estimate … issued …. This is a quotation…". */
  issuedNote?: string | null;
  /** The private-link notice is public-quote only — the logged-in portal hides it. */
  showSecureNotice?: boolean;
  /** When set, a "Decline this estimate" link is appended inline to the legal
   *  footer sentence (the artifact's `.dfoot` pattern). */
  onDecline?: () => void;
}) {
  const contactPhone = phone?.trim();
  const dialNumber = contactPhone?.replace(/[^\d+]/g, '') ?? '';
  const hasContact = /\d/.test(dialNumber);
  const legalParts = [companyName, companyAddress, license ? `License ${license}` : null]
    .map((p) => p?.trim())
    .filter((p): p is string => Boolean(p));
  if (!hasContact && !portalHref && legalParts.length === 0 && !issuedNote?.trim() && !onDecline)
    return null;

  // Contact URIs must be dial-safe: keep a leading + then digits only, so the
  // stored display format ("(312) 555-0142") doesn't leak parens/spaces.
  const telHref = `tel:${dialNumber}`;
  const smsHref = `sms:${dialNumber}`;

  return (
    <div className="mt-8 space-y-4">
      {hasContact ? (
        <div className="rounded-[10px] border border-[#14141A]/[0.08] bg-white p-5 sm:p-6">
          <p className="font-medium text-[#17171A]">Questions before you decide?</p>
          <p className="mt-1 text-[13.5px] text-[#63646B]">
            Talk to the people who put this together — no call center.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {hasContact ? (
              <Button asChild variant="outline" size="sm">
                <a href={telHref}>
                  <Phone className="mr-1.5 size-4" />
                  {formatPhoneDisplay(contactPhone ?? '')}
                </a>
              </Button>
            ) : null}
            {hasContact ? (
              <Button asChild variant="outline" size="sm">
                <a href={smsHref}>
                  <MessageSquare className="mr-1.5 size-4" />
                  Send a text
                </a>
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Private-link security notice (.pubsecure) — sits above the account
          prompt, matching the artifact's pubfoot order. Public quote only. */}
      {showSecureNotice ? (
        <div className="flex items-start gap-2.5 rounded-[10px] border border-[#14141A]/[0.08] px-4 py-[14px]">
          <Lock className="mt-0.5 size-4 shrink-0 text-[#95969D]" />
          <p className="text-[12.5px] leading-[1.55] text-[#63646B]">
            This is a private link created for you. Please don&apos;t forward it — anyone with the
            link can view this estimate.
            {expiresLabel ? ` It stops working after ${expiresLabel}.` : ''}
          </p>
        </div>
      ) : null}

      {portalHref ? (
        <div className="rounded-[10px] border border-dashed border-[#14141A]/[0.08] p-5 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="font-medium text-[#17171A]">See everything in one place</p>
              <p className="mt-1 text-[13.5px] text-[#63646B]">
                Past visits, invoices, and what&apos;s still under warranty — no password to
                remember, we text you a link.
              </p>
            </div>
            <Button
              asChild
              size="sm"
              className="shrink-0 bg-[var(--ss-accent)] text-white hover:bg-[var(--ss-accent-strong)]"
            >
              <a href={portalHref}>Set up my account</a>
            </Button>
          </div>
        </div>
      ) : null}

      {/* Document footer sentence (.dfoot) + legal line (.publegal). The
          "Decline this estimate" link sits inline at the end of the .dfoot
          sentence (accent), matching the artifact. */}
      {issuedNote?.trim() || legalParts.length > 0 || onDecline ? (
        <div className="space-y-1 pt-1 text-center">
          {issuedNote?.trim() || onDecline ? (
            <p className="text-[12.5px] leading-[1.5] text-[#95969D]">
              {issuedNote?.trim()}
              {onDecline ? (
                <>
                  {issuedNote?.trim() ? ' ' : null}
                  <button
                    type="button"
                    onClick={onDecline}
                    className="font-medium text-[var(--ss-accent-strong)] underline-offset-[3px] hover:underline"
                  >
                    Decline this estimate
                  </button>
                </>
              ) : null}
            </p>
          ) : null}
          {legalParts.length > 0 ? (
            <p className="text-[12px] leading-[1.6] text-[#95969D]">{legalParts.join(' · ')}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
